import { FC, useMemo, useState } from 'react';

import { Dialogs } from 'analytics/events/dialog';

import { TwoWayMessagesFetcher } from 'fetchers/TwoWayMessagesFetcher';

import { useTicketClose } from 'hooks/useTicketClose';
import { ResolveTicketsData, useTicketResolve } from 'hooks/useTicketResolve';

import { MessageDialog } from 'components/UIkit/atoms/Dialog';

interface Props {
  isOpen: boolean;
  closeDialog: () => void;
  resolveTicketsData: ResolveTicketsData;
  action?: 'close' | 'resolve';
}

const useUndeliveredMessageToPatientDialog = ({
  isOpen,
  closeDialog,
  resolveTicketsData,
  action = 'resolve'
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const ticketResolve = useTicketResolve();
  const ticketClose = useTicketClose();

  const { onClick, textNoun, textDelete } = useMemo((): {
    onClick: () => Promise<void>;
    textNoun: string;
    textDelete: string;
  } => {
    const ticketIds = resolveTicketsData.tickets.map((ticket) => ticket.id);

    const createClickHandler = (handler: () => Promise<unknown>) => async () => {
      try {
        setIsLoading(true);
        await TwoWayMessagesFetcher.deleteUndeliveredMessages(ticketIds);
        closeDialog();
        await handler();
      } finally {
        closeDialog();
        setIsLoading(false);
      }
    };

    if (action === 'close') {
      return {
        textDelete: 'Delete & Close',
        textNoun: 'Closing',
        onClick: createClickHandler(() =>
          ticketClose({
            tickets: resolveTicketsData.tickets,
            patientId: resolveTicketsData.patientId
          })
        )
      };
    }

    return {
      textDelete: 'Delete & Resolve',
      textNoun: 'Resolving',
      onClick: createClickHandler(() => ticketResolve(resolveTicketsData))
    };
  }, [action, ticketClose, ticketResolve, resolveTicketsData, closeDialog]);

  return {
    onClick,
    textNoun,
    textDelete,
    isLoading,
    isOpen,
    closeDialog
  };
};

export const UndeliveredMessageToPatientDialog: FC<Props> = (props) => {
  const { onClick, textNoun, textDelete, isLoading, isOpen, closeDialog } =
    useUndeliveredMessageToPatientDialog(props);

  return (
    <MessageDialog
      id={Dialogs.UndeliveredMessageToPatient}
      isOpen={isOpen}
      handleClose={closeDialog}
      title="Undelivered Message(s) to Patient"
      primaryActionProps={{ text: textDelete, onClick, disabled: isLoading }}
      secondaryActionProps={{ text: 'Cancel', onClick: closeDialog }}
    >
      At least one message was not successfully delivered to the patient. {textNoun} will remove the
      undelivered message from the ticket / task, and the resulting EMR PDF.
    </MessageDialog>
  );
};
